<i18n src="./resources/locales.json"></i18n>

<template>
  <div class="ecs-bp-stats-card" :dark="$vuetify.theme.dark">
    <div class="bp-title xtium-title-regular">
      {{ stats.businessProcessName }}
    </div>

    <div v-once class="graph-title">{{ $t('interactionHandling') }}</div>

    <div ref="chart" class="graph">
      <div class="legend">
        <div v-for="(item, index) in graphDataset" :key="index" class="legend-item">
          <div class="legend-item-name">{{ item.shortTitle }}</div>
          <div class="legend-item-color" :style="{ backgroundColor: item.color }"></div>
          <div class="legend-item-value">
            {{ kpiFormat(item.value, 'number') }}
          </div>
        </div>
      </div>
    </div>

    <div v-once class="graph-title">{{ $t('queueMetrics') }}</div>

    <div class="stat-group">
      <div v-once class="stat-group-title">
        {{ $t('businessProcessWaitingDeferrableInteractions') }}
      </div>
      <div class="stat-group-value">
        {{ kpiFormat(waitingDeferrableInteractions.value, 'number') }}
      </div>
    </div>

    <div class="stat-group">
      <div v-once class="stat-group-title">
        {{ $t('businessProcessAbandonedPercentage') }}
      </div>
      <div class="stat-group-value">
        {{ kpiFormat(abandonedPercentage.value, 'percentage') }}
      </div>
    </div>
    <div class="stat-group">
      <div v-once class="stat-group-title">
        {{ $t('businessProcessAverageAgentHandlingTime') }}
      </div>
      <v-tooltip color="surface lighten-2" top>
        <template v-slot:activator="{ on }">
          <div class="stat-group-value" v-on="on">
            {{ kpiFormat(averageAgentHandlingTime.value, 'duration') }}
          </div>
        </template>
        <span>
          {{ kpiFormat(averageAgentHandlingTime.value, 'duration', true) }}
        </span>
      </v-tooltip>
    </div>
    <div class="stat-group">
      <div v-once class="stat-group-title">
        {{ $t('businessProcessAverageAnswerTime') }}
      </div>
      <div class="stat-group-value">
        {{ kpiFormat(averageAnswerTime.value, 'duration') }}
      </div>
    </div>
    <div class="stat-group">
      <div v-once class="stat-group-title">
        {{ $t('businessProcessLongestWaitingTime') }}
      </div>
      <div class="stat-group-value">
        {{ kpiFormat(longestWaitingTime.value, 'duration') }}
      </div>
    </div>

    <div class="stat-group">
      <div v-once class="stat-group-title">
        {{ $t('businessProcessIdleAgents') }}
      </div>
      <div class="stat-group-value">
        {{ kpiFormat(idleAgents.value, 'number') }}
      </div>
    </div>

    <div class="stat-group">
      <div v-once class="stat-group-title">
        {{ $t('businessProcessServiceLevel') }}
      </div>
      <div class="stat-group-value">
        {{ kpiFormat(serviceLevel.value, 'percentage') }}
      </div>
    </div>

    <div class="stat-group">
      <div v-once class="stat-group-title">
        {{ $t('businessProcessCallbacksProcessed') }}
      </div>
      <div class="stat-group-value">
        {{ kpiFormat(callbacksProcessed.value, 'number') }}
      </div>
    </div>

    <div class="stat-group">
      <div v-once class="stat-group-title">
        {{ $t('businessProcessCallbacksScheduled') }}
      </div>
      <div class="stat-group-value">
        {{ kpiFormat(callbacksScheduled.value, 'number') }}
      </div>
    </div>
  </div>
</template>

<script>
import DonutGraph from '@/components/ecs-agent-stats/ecs-bp-stats-card/d3-donut-graph/d3-donut-graph';
import EcsKpiFormatter from '@/mixins/ecs-kpi-formatter/EcsKpiFormatter';

export default {
  name: 'EcsBpStatsCard',

  mixins: [EcsKpiFormatter],

  props: {
    stats: {
      type: Object,
      default: () => {}
    }
  },

  data() {
    return {
      graph: {
        type: Object,
        default: () => {}
      }
    };
  },

  computed: {
    abandonedInteractions() {
      return this.stats.businessProcessAbandonedInteractions;
    },
    answeredInteractions() {
      return this.stats.businessProcessAnsweredInteractions;
    },
    overflowInteractions() {
      return this.stats.businessProcessOverflowInteractions;
    },
    waitingInteractions() {
      return this.stats.businessProcessWaitingInteractions;
    },
    waitingDeferrableInteractions() {
      return this.stats.businessProcessWaitingDeferrableInteractions;
    },
    abandonedPercentage() {
      return this.stats.businessProcessAbandonedPercentage;
    },
    averageAgentHandlingTime() {
      return this.stats.businessProcessAverageAgentHandlingTime;
    },
    averageAnswerTime() {
      return this.stats.businessProcessAverageAnswerTime;
    },
    longestWaitingTime() {
      return this.stats.businessProcessLongestWaitingTime;
    },
    idleAgents() {
      return this.stats.businessProcessIdleAgents;
    },
    serviceLevel() {
      return this.stats.businessProcessServiceLevel;
    },
    callbacksProcessed() {
      return this.stats.callbackProcessedInShift;
    },
    callbacksScheduled() {
      return this.stats.callbackToBeProcessed;
    },
    graphDataset() {
      return [
        {
          title: this.$t('businessProcessAnsweredInteractions'),
          shortTitle: this.$t('answered'),
          color: '#4ea96d',
          value: this.answeredInteractions.value || 0
        },
        {
          title: this.$t('businessProcessAbandonedInteractions'),
          shortTitle: this.$t('abandoned'),
          color: '#f44336',
          value: this.abandonedInteractions.value || 0
        },
        {
          title: this.$t('businessProcessOverflowInteractions'),
          shortTitle: this.$t('overflowed'),
          color: '#ffc107',
          value: this.overflowInteractions.value || 0
        },
        {
          title: this.$t('businessProcessWaitingInteractions'),
          shortTitle: this.$t('waiting'),
          color: 'var(--v-xtium-robin-egg-blue)',
          value: this.waitingInteractions.value || 0
        }
      ];
    }
  },

  watch: {
    stats: {
      handler() {
        if (this.graph && this.graph.updateDataset) {
          this.graph.updateDataset(this.graphDataset);
        }
      },
      deep: true
    }
  },

  beforeDestroy() {
    this.graph.remove();
    this.graph = null;
    delete this.graph;
  },

  mounted() {
    this.graph = new DonutGraph(this.$refs.chart, this.graphDataset);
    this.graph.render();
  }
};
</script>

<style src="./scoped.scss" lang="scss" scoped />
